import React from 'react';
import DashboardChart from '../components/DashboardChart';
import { Loader } from '../Loader';
import {
  aggregateSeriesByDate,
  canShowPeriodTotal,
  describeCoverageGaps,
  formatNumber,
  isCoverageIncomplete,
  latestSeriesPoint,
  sumSeries,
  VALUE_KIND_STOCK,
} from './analyticsUtils';

const CHART_BACKGROUND_COLOR = 'rgba(102, 100, 195, 0.3)';
const CHART_BORDER_COLOR = 'rgba(102, 100, 195, 1)';

// Time series panel for GET /v1/analytics/datasets/{name}/series.
//
// Correctness rules enforced here:
//  1. A dataset whose valueKind is STOCK is a standing total as of each date.
//     Adding those dates together is meaningless, so no "total over the period"
//     figure is offered or displayed for a STOCK dataset - the latest standing
//     value is shown instead.
//  2. When coverage.complete is false the missing dates are named in a visible
//     warning, because any day over day reading across a gap is wrong.
//  3. Loading, error and empty are three separate states with three separate
//     renderings. A failed request must never read as "no data".
function AnalyticsSeriesPanel({
  datasets = [],
  selectedDatasetName = '',
  onSelectDataset,
  series,
  loading = false,
  error = null,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  dimension = '',
  dimensionOptions = [],
  onDimensionChange,
  rangeError,
}) {
  const points = series?.points || [];
  const aggregated = aggregateSeriesByDate(points);
  const valueKind = series?.valueKind;
  const showPeriodTotal = canShowPeriodTotal(valueKind);
  const latest = latestSeriesPoint(aggregated);
  const selectedDataset = datasets.find((entry) => entry.datasetName === selectedDatasetName);
  const coverage = series?.coverage;
  // Stats describe the loaded series, so they are meaningless while a request
  // is in flight or after one has failed.
  const showStats = !loading && !error && !!series;

  return (
    <div className="analytics-series-panel">
      <div className="analytics-series-controls">
        <div className="analytics-control">
          <label htmlFor="analytics-dataset">Dataset</label>
          <select
            id="analytics-dataset"
            className="form-select"
            value={selectedDatasetName}
            onChange={(event) => onSelectDataset(event.target.value)}
          >
            {datasets.length === 0 && <option value="">No datasets available</option>}
            {datasets.map((dataset) => (
              <option key={dataset.datasetName} value={dataset.datasetName}>
                {dataset.displayName || dataset.datasetName}
              </option>
            ))}
          </select>
        </div>

        <div className="analytics-control">
          <label htmlFor="analytics-from-date">From</label>
          <input
            id="analytics-from-date"
            className="form-control"
            type="date"
            value={fromDate || ''}
            onChange={(event) => onFromDateChange(event.target.value)}
          />
        </div>

        <div className="analytics-control">
          <label htmlFor="analytics-to-date">To</label>
          <input
            id="analytics-to-date"
            className="form-control"
            type="date"
            value={toDate || ''}
            onChange={(event) => onToDateChange(event.target.value)}
          />
        </div>

        {dimensionOptions.length > 0 && (
          <div className="analytics-control">
            <label htmlFor="analytics-dimension">Dimension</label>
            <select
              id="analytics-dimension"
              className="form-select"
              value={dimension}
              onChange={(event) => onDimensionChange(event.target.value)}
            >
              <option value="">All</option>
              {dimensionOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {rangeError && (
        <div className="analytics-warning" role="alert" data-testid="analytics-range-error">
          {rangeError}
        </div>
      )}

      {selectedDataset?.description && (
        <p className="analytics-dataset-description">{selectedDataset.description}</p>
      )}

      {isCoverageIncomplete(coverage) && (
        <div className="analytics-warning" role="alert" data-testid="analytics-coverage-warning">
          <strong>Incomplete data.</strong> {describeCoverageGaps(coverage)} Day over day
          movements across these gaps are not reliable.
        </div>
      )}

      {showStats && (
        <div className="analytics-series-stats">
          {valueKind && (
            <span className="analytics-stat" data-testid="analytics-value-kind">
              Value kind: {valueKind}
            </span>
          )}
          {latest && (
            <span className="analytics-stat" data-testid="analytics-latest-value">
              Latest ({latest.label}): {formatNumber(latest.data)}
            </span>
          )}
          {showPeriodTotal && (
            <span className="analytics-stat" data-testid="analytics-period-total">
              Total over period: {formatNumber(sumSeries(aggregated))}
            </span>
          )}
          {valueKind === VALUE_KIND_STOCK && (
            <span className="analytics-stat analytics-stat-note" data-testid="analytics-stock-note">
              Standing total per date - a sum over the period is not meaningful.
            </span>
          )}
        </div>
      )}

      <div className="analytics-chart-wrapper">
        {loading ? (
          <div className="analytics-chart-loader" data-testid="analytics-series-loading">
            <Loader width="64" />
          </div>
        ) : error ? (
          <p className="analytics-error" role="alert" data-testid="analytics-series-error">
            The chart could not be loaded: {error}
          </p>
        ) : aggregated.length === 0 ? (
          <p className="analytics-empty" data-testid="analytics-series-empty">
            No data points for the selected dataset and date range.
          </p>
        ) : (
          <DashboardChart
            type="line"
            textTitle={selectedDataset?.displayName || selectedDatasetName}
            chartData={aggregated}
            label={selectedDataset?.displayName || selectedDatasetName}
            backgroundColor={CHART_BACKGROUND_COLOR}
            borderColor={CHART_BORDER_COLOR}
            borderWidth={2}
          />
        )}
      </div>
    </div>
  );
}

export default AnalyticsSeriesPanel;
